/**
 * Companies.tsx — lista firm tenanta na ConfigurableTable; kolumny DATA-DRIVEN z katalogu
 * (skill `kartoteka-listify`, wzorzec z `PurchaseInvoices.tsx`/`Contractors.tsx`).
 *
 * Kolumny rdzenne buduje `buildCoreColumns` z katalogu pól (`GET /system-modules/companies/
 * fields`): pole z „Pokaż na liście" (showInList) + typ renderowalny = kolumna; klucz w wierszu
 * odpowiedzi wyliczany mechanicznie ze sluga (camelCase) — ZERO mapy slug→klucz per strona
 * (usunięta dawna `CORE_COLUMN_ID_TO_SLUG`). Firma nie ma pól company_id/owner/category w
 * katalogu, więc `excludeSlugs` jest puste.
 *
 * Rejestr nadpisań (`COMPANY_STATIC_OVERRIDES` + `makeLegalNameOverride`, scalane w
 * `companyOverrides`) niesie TRZY bogate renderery:
 * „Nazwa prawna" (link + nazwa skrócona pod spodem), „Główna firma" (badge bursztynowy) i
 * „Aktywna" (badge zielony/szary). Filtrowalność kolumn rdzennych liczy SERWER (automat
 * `autoHeaderFilterColumns` → `meta.filterable`, 16 pól; `groups` na blocklist), a front wpina ją
 * przez `resolveFilterable` (bliźniak `resolveSortable`) — generyczny `buildCoreColumns` sam
 * dokleja `headerFilter` do pól filtrowalnych, a `/companies/distinct` czerpie tę samą listę
 * z deskryptora. Nadpisania is_primary/is_active dokładają tylko własny widżet select z etykietą
 * Tak/Nie; reszta pól idzie generykiem.
 *
 * Widoczność: brak układu → wszystkie dostępne widoczne; układ jest DOSŁOWNY. `?columns=`
 * budowany z aktualnie widocznych kolumn (`computeVisibleColumnParams`) — `is_primary` zawsze
 * (steruje blokadą usuwania firmy), `short_name` towarzyszy `legal_name` (podpis pod nazwą).
 *
 * Paginacja: backend (silnik list) domyślnie tnie na `limit=50` — strona wpina pełny wzorzec
 * `useServerListControls` (sort serwerowy + `page`/`pageSize`/`totalItems` na `ConfigurableTable`)
 * zamiast dawnego nieograniczonego fetcha, bo firm może być więcej niż jedna strona domyślnego
 * limitu na dużych tenantach, a odpowiedź niesie już pełny kontrakt silnika
 * (`limit`/`offset`/`total`/`meta.sortable`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Building2, Pencil, Plus, Star } from 'lucide-react';

import { Button } from '@/components/Button';
import { type Company } from '@/components/CompanyDetailDrawer';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import { COMPOSITE_TEXT_FILTER_OPERATORS } from '@/components/configurable-table/filter-row/filter-operators';
import {
  type ListResponseMeta,
  useServerListControls,
} from '@/components/configurable-table/useServerListControls';
import { GroupChipBar } from '@/components/groups/GroupChipBar';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import {
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type CoreColumnOverride,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { useEngineListExport } from '@/lib/list-export/useEngineListExport';

const COMPANIES_KEY = 'companies';

interface CompanyRow extends Company {
  email: string | null;
  phone: string | null;
  website: string | null;
  /**
   * Wartości pól dodatkowych (ext) — obecne TYLKO gdy backend odpowiada w trybie
   * projekcji `?columns=` i zażądano ≥1 aktywnego pola ext (klucz `ext` pominięty gdy
   * brak żądanych pól ext; kontrakt `GET /api/companies?columns=`).
   */
  ext?: Record<string, unknown>;
}

interface ListResponse {
  companies: CompanyRow[];
  limit: number;
  offset: number;
  total: number;
  /** Slugi kolumn CORE sortowalnych serwerowo (silnik list) — patrz `useServerListControls`. */
  meta?: ListResponseMeta;
  /** Etykiety pól `reference` (sibling do wierszy) — patrz `ExtRefLabels`. */
  extRefLabels?: ExtRefLabels;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Mapper raw boolean → label „Tak"/„Nie" dla filtra (distinct zwraca boolean rzutowany na 'true'/'false' tekst). */
function transformBoolean(t: (key: string) => string): (value: string | null) => string {
  return (value) => {
    if (value === 'true') return t('common.yes');
    if (value === 'false') return t('common.no');
    return t('common.lists.emptyValue');
  };
}

/**
 * Część rejestru nadpisań NIEZALEŻNA od kontekstu komponentu (bez `withMenuItem`) — może żyć
 * poza komponentem jak w `PurchaseInvoices.tsx`. „Główna firma"/„Aktywna" mają bogaty render
 * (badge) + własny widżet filtra select (Tak/Nie). Pozostałe kolumny rdzenne idą generykiem
 * `buildCoreColumns` (render + filtr nagłówka sterowany `resolveFilterable`).
 */
const COMPANY_STATIC_OVERRIDES: Record<string, CoreColumnOverride<CompanyRow>> = {
  is_primary: ({ field, t, resolveSortable }) => ({
    id: field.slug,
    accessor: (row: CompanyRow) => (row.isPrimary ? 'true' : 'false'),
    header: field.name,
    defaultVisible: true,
    defaultWidth: 120,
    sortable: resolveSortable?.(field) ?? true,
    headerFilter: {
      category: 'select',
      fieldSlug: 'is_primary',
      transformLabel: transformBoolean(t),
    },
    renderCell: (_value, row) =>
      row.isPrimary ? (
        <span
          className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          data-testid={`companies-row-${row.id}-primary-badge`}
        >
          <Star size={10} aria-hidden="true" />
          {t('tenant.companies.badge.primary')}
        </span>
      ) : (
        <span className="text-text-tertiary">—</span>
      ),
  }),
  is_active: ({ field, t, resolveSortable }) => ({
    id: field.slug,
    accessor: (row: CompanyRow) => (row.isActive ? 'true' : 'false'),
    header: field.name,
    defaultVisible: true,
    defaultWidth: 120,
    sortable: resolveSortable?.(field) ?? true,
    headerFilter: {
      category: 'select',
      fieldSlug: 'is_active',
      transformLabel: transformBoolean(t),
    },
    renderCell: (_value, row) =>
      row.isActive ? (
        <span className="inline-flex rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
          {t('tenant.companies.badge.active')}
        </span>
      ) : (
        <span className="inline-flex rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          {t('tenant.companies.badge.inactive')}
        </span>
      ),
  }),
};

/**
 * „Nazwa prawna" — jedyne nadpisanie zależne od kontekstu komponentu (`withMenuItem`, dokleja
 * `?mi=<id>` do nawigacji w głąb). Link + nazwa skrócona pod spodem (jeśli ustawiona);
 * nieukrywalna (`hideable: false`) — to podstawowa kolumna identyfikująca wiersz.
 */
function makeLegalNameOverride(
  withMenuItem: (path: string) => string,
): CoreColumnOverride<CompanyRow> {
  return ({ field, accessor, resolveSortable }) => ({
    id: field.slug,
    accessor,
    header: field.name,
    defaultVisible: true,
    defaultWidth: 280,
    hideable: false,
    sortable: resolveSortable?.(field) ?? true,
    // Filtr kolumny ZŁOŻONEJ „Nazwa prawna" — grupa OR: syntetyczny slug `legal_name_full`
    // (backend rozwija na `legal_name`/`short_name`). `conditionOnly` — slug filtra-tylko.
    // `orGroupFields` wygasza wzajemnie z ewentualnymi osobnymi kolumnami nazwy/skrótu.
    headerFilter: {
      category: 'text',
      fieldSlug: 'legal_name_full',
      conditionOnly: true,
      operators: COMPOSITE_TEXT_FILTER_OPERATORS,
      orGroupFields: ['legal_name', 'short_name'],
    },
    renderCell: (_value, row) => (
      <div onClick={(e) => e.stopPropagation()}>
        <Link
          to={withMenuItem(`/companies/${row.id}`)}
          className="font-medium text-accent-700 hover:underline dark:text-accent-300"
        >
          {row.legalName}
        </Link>
        {row.shortName && <div className="text-xs text-text-tertiary">{row.shortName}</div>}
      </div>
    ),
  });
}

export function Companies() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * Filtr chipów nad listą = GRUPY ze słownika `company_groups` (domknięcie projektu grup dla
   * firm, 2026-08-19). Wiele grup naraz, semantyka OR.
   */
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  // Zaznaczenie wierszy dla akcji zbiorczych (menu „Akcje" + prawy klik).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Wymuszony layout listy z pozycji Kreatora menu, z której user przyszedł
  // (kartoteka-layout-per-menu) — ta sama zmienna `tableId` musi iść do WSZYSTKICH
  // konsumentów niżej (ListTopWidgets, ConfigurableTable), inaczej widżety i tabela
  // renderują dwa różne layouty.
  const forcedListLayoutId = useForcedListLayoutId(COMPANIES_KEY);
  const tableId = forcedListLayoutId
    ? `${COMPANIES_KEY}::layout-${forcedListLayoutId}`
    : COMPANIES_KEY;

  // Dokleja `?mi=<id>` do nawigacji w głąb (nowy/detal), żeby formularz firmy
  // znał kontekst pozycji menu (viewRef) — URL detalu jest osobną nawigacją, nie
  // dziedziczy query stringa listy.
  const withMenuItem = useWithMenuItem();

  // Sort serwerowy + paginacja (silnik list). id kolumny = slug wszędzie (firma nie ma kolumn
  // wirtualnych typu „company"/proces), więc `columnIdToSortSlug` nie jest potrzebne.
  const controls = useServerListControls();
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Szybkie filtry (presety z list_config) — chipy nad listą.
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  // Tryb widoku + tryby dostępne w bieżącym layoucie (raport tabeli) — wspólny hook.
  const viewModes = useListViewMode();
  // Domyślny preset (isDefault) aktywny przy pierwszym wejściu.
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);
  // Filtry per kolumna z popupu w nagłówku.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  // Filtry "Szukaj…" w wierszu pod nagłówkami (filter row).
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  // Zmiana filtra/grupy/szybkiego filtra wraca na pierwszą stronę (paginacja OFFSET) —
  // inaczej user może utknąć na stronie, której zawężony wynik już nie ma.
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );
  const handleColumnFiltersChange = useCallback(
    (next: ColumnFilters) => {
      setColumnFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );

  // Katalog pól modułu `companies` (core + ext) — źródło WSZYSTKICH kolumn (rdzennych i
  // dodatkowych) generowanych dynamicznie poniżej.
  const fieldsQ = useSystemModuleFields('companies');
  // `getRefLabels` — getter stabilny (patrz `useExtRefLabelsGetter`), aktualizowany niżej PO `companiesQ`.
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();

  const extColumns = useMemo<ColumnDefinition<CompanyRow>[]>(
    () => buildExtColumns<CompanyRow>(fieldsQ.data ?? [], t, getRefLabels),
    [fieldsQ.data, t, getRefLabels],
  );

  // Rejestr nadpisań kompletny (statyczne + „Nazwa prawna" zależna od `withMenuItem`).
  const companyOverrides = useMemo<Record<string, CoreColumnOverride<CompanyRow>>>(
    () => ({ ...COMPANY_STATIC_OVERRIDES, legal_name: makeLegalNameOverride(withMenuItem) }),
    [withMenuItem],
  );

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; garstka przez rejestr nadpisań).
  // Firma nie ma company_id/owner/category w katalogu — `excludeSlugs` niepotrzebne.
  // Builder z getterem etykiet odwołań jako parametrem — tabela dostaje etykiety z bieżącej strony
  // listy, eksport CSV (E16) etykiety z odpowiedzi eksportu (wszystkie wyeksportowane wiersze).
  const buildCompanyCoreColumns = useCallback(
    (getLabels: () => ExtRefLabels | undefined): ColumnDefinition<CompanyRow>[] =>
      buildCoreColumns<CompanyRow>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels: getLabels,
        overrides: companyOverrides,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
      }),
    [
      fieldsQ.data,
      t,
      companyOverrides,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
    ],
  );
  const coreColumns = useMemo(
    () => buildCompanyCoreColumns(getRefLabels),
    [buildCompanyCoreColumns, getRefLabels],
  );

  const columns = useMemo<ColumnDefinition<CompanyRow>[]>(
    () => [...coreColumns, ...extColumns],
    [coreColumns, extColumns],
  );

  // Mapa id kolumny → slug backendu dla `?columns=` — id = slug wszędzie (buildCoreColumns).
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of coreColumns) map[c.id] = c.id;
    return map;
  }, [coreColumns]);

  // Slugi aktualnie widocznych kolumn (wspólny helper) → `?columns=`, żeby zapytanie
  // listy ciągnęło tylko renderowane dane. `is_primary` ZAWSZE (steruje blokadą
  // usuwania firmy — przycisk + guard — więc potrzebny nawet gdy kolumnę „Główna"
  // ukryto). Towarzysz legal_name→short_name: podpis „krótka nazwa" pod nazwą firmy
  // renderuje `row.shortName`, a to nie osobna kolumna.
  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns: listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet: new Set(extColumns.map((c) => c.id)),
        alwaysSlugs: ['is_primary'],
        companions: [{ whenSlug: 'legal_name', addSlug: 'short_name' }],
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extColumns,
    ],
  );

  // Parametry filtrów listy BEZ stronicowania i projekcji (grupy, filtry kolumn, szybki filtr,
  // sort) — jedno źródło dla zapytania listy i eksportu CSV (E16), żeby plik miał te same filtry.
  // `includeCalendar`: miesiąc widoku kalendarza to nawigacja, nie filtr — tylko zapytanie listy.
  function buildListFilterParams({
    includeCalendar,
  }: {
    includeCalendar: boolean;
  }): URLSearchParams {
    const params = new URLSearchParams();
    for (const code of selectedGroups) params.append('groups', code);

    const merged: ColumnHeaderFilter[] = [];
    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }
    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue;
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }
    // Aktywny szybki filtr — warunki presetu (pole=columnId → fieldSlug).
    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) {
      for (const cond of activeQf.filters) {
        const col = columns.find((c) => c.id === cond.field);
        const fieldSlug = col?.headerFilter?.fieldSlug;
        if (!fieldSlug) continue;
        merged.push({
          field: fieldSlug,
          mode: 'condition',
          operator: cond.operator as ColumnHeaderFilter['operator'],
          conditionValue: cond.value == null ? '' : String(cond.value),
        });
      }
    }
    // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika) —
    // tylko dla listy, nie dla eksportu.
    if (includeCalendar && controls.calendarHeaderFilter) {
      merged.push(controls.calendarHeaderFilter);
    }
    if (merged.length > 0) {
      params.set('headerFilters', JSON.stringify(merged));
    }
    if (controls.queryParams.sort) params.set('sort', controls.queryParams.sort);
    params.set('order', controls.queryParams.order);
    return params;
  }

  const queryKey = [
    COMPANIES_KEY,
    {
      groups: selectedGroups.length > 0 ? [...selectedGroups].sort() : null,
      headerFilters,
      columnFilters: debouncedColumnFilters,
      quickFilterId: activeQuickFilterId,
      columns: visibleColumnParams,
      calendarRange: controls.calendarRange,
      ...controls.queryParams,
    },
  ];
  const companiesQ = useQuery({
    queryKey,
    // Trzyma poprzednie dane podczas refetchu wywołanego zmianą klucza (widoczne kolumny/strona
    // się „ustalają" asynchronicznie) — bez tego lista miga loaderem przy każdej zmianie.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = buildListFilterParams({ includeCalendar: true });
      // Projekcja kolumn — tylko widoczne kolumny (core+ext), zawsze z `id`.
      if (visibleColumnParams.length > 0) {
        params.set('columns', visibleColumnParams.join(','));
      }
      params.set('limit', String(controls.queryParams.limit));
      params.set('offset', String(controls.queryParams.offset));

      const url = params.toString() ? `/companies?${params.toString()}` : '/companies';
      const res = await api.get<ListResponse>(url);
      return res.data;
    },
    staleTime: 30_000,
  });
  setRefLabels(companiesQ.data?.extRefLabels);
  // `meta.sortable`/`meta.filterable` w stan przez EFEKT, nie w renderze (to useState → wywołanie
  // w renderze pętli). Filtrowalność kolumn rdzennych liczy serwer (automat filtra nagłówka).
  useEffect(() => {
    setSortableSlugs(companiesQ.data?.meta?.sortable);
  }, [companiesQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(companiesQ.data?.meta?.filterable);
  }, [companiesQ.data?.meta?.filterable]);

  function handleEdit(company: CompanyRow) {
    navigate(withMenuItem(`/companies/${company.id}`));
  }

  const companies = companiesQ.data?.companies ?? [];

  // Eksport CSV (E16) — dokładnie to, co lista. „Eksport listy": te same filtry co zapytanie listy
  // (`buildListFilterParams`: grupy, filtry kolumn, szybki filtr, sort), wszystkie strony.
  // „Eksport zaznaczonych": tylko wskazane ID (+ sort listy). Plik zawiera WYŁĄCZNIE kolumny
  // widoczne w tabeli, w jej kolejności i z jej nagłówkami.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);
  const extSlugSet = useMemo(() => new Set(extColumns.map((c) => c.id)), [extColumns]);
  const listExport = useEngineListExport<CompanyRow>({
    target: 'companies',
    filename: 'firmy',
    buildFilterParams: () => buildListFilterParams({ includeCalendar: false }),
    columns,
    visibleColumnIds,
    // Dopóki katalog pól i układ listy się ładują, eksport nie startuje (niepełna lista kolumn).
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extSlugSet.has(id) ? id : undefined),
    // Etykiety odwołań (pola `reference`) z odpowiedzi eksportu — obejmują WSZYSTKIE
    // wyeksportowane wiersze, nie tylko bieżącą stronę listy. Te same buildery co tabela.
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return [
        ...buildCompanyCoreColumns(getLabels),
        ...buildExtColumns<CompanyRow>(fieldsQ.data ?? [], t, getLabels),
      ];
    },
    // Komórka „Nazwa prawna" to link + podpis nazwy skróconej — do pliku sama nazwa prawna.
    // „Główna firma": ekran pokazuje plakietkę albo „—"; bez nadpisania pusta komórka spadłaby
    // na surową wartość accessora („false") — do pliku plakietka albo pusto, jak na ekranie.
    textOverrides: {
      legal_name: (row) => row.legalName ?? '',
      is_primary: (row) => (row.isPrimary ? t('tenant.companies.badge.primary') : ''),
    },
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: t('tenant.companies.title'),
  });

  // Akcje listy (PHASE 25). FIRM NIE DA SIĘ USUWAĆ w apce (decyzja właścicielki) —
  // akcja „Usuń" jest wykluczona dla `companies` w katalogu (`excludedKartoteki`), więc
  // nie pojawia się w menu; nie ma też natywnego kosza w wierszu. Eksporty (zaznaczonych
  // i całej listy) składa strona z widocznych kolumn (`handlers`); importu firmy nie mają →
  // „wkrótce". Bez bramki „Wszystkie firmy" i bez `rowCatalogActionItems`.
  const companyActions = useListActions({
    kartotekaKey: 'companies',
    entity: 'companies',
    queryKey: [COMPANIES_KEY],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    handlers: { ...listExport.handlers },
    testIdPrefix: 'companies',
  });

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-6" data-testid="companies-page">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text-primary">{t('tenant.companies.title')}</h1>
          <p className="mt-1 text-sm text-text-tertiary">{t('tenant.companies.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="companies" />
          <ListActionsMenu {...companyActions.menuProps} />
          <ListBuilderMenu slug="companies" testIdPrefix="companies" />
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(withMenuItem('/companies/new'))}
            leftIcon={<Plus size={16} aria-hidden="true" />}
            data-testid="companies-add-btn"
          >
            {t('tenant.companies.addBtn')}
          </Button>
        </div>
      </header>

      <div className="shrink-0">
        <ListTopWidgets tableId={tableId} categoryId={null} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {fieldsQ.isError ? (
          <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
        ) : (
          <ConfigurableTable<CompanyRow>
            tableId={tableId}
            testIdPrefix="companies"
            showOrdinal
            fillHeight
            {...viewModes.tableProps}
            /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): chipy grup po lewej,
               baton odśwież po prawej; presety w osobnym rzędzie pod spodem. */
            listToolbar={
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Chipy filtra = GRUPY ze słownika (wielokrotny wybór, OR) — zastąpiły chipy kategorii. */}
                  <GroupChipBar
                    dictionaryCode="company_groups"
                    selected={selectedGroups}
                    onChange={(next) => {
                      setSelectedGroups(next);
                      controls.resetPage();
                    }}
                    className="flex-1"
                    data-testid="companies-group-chip-bar"
                  />
                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    <ListFilterBar
                      tableId={tableId}
                      columns={columns}
                      search=""
                      onSearchChange={() => {}}
                      showSearch={false}
                      headerFilters={headerFilters}
                      onHeaderFiltersChange={handleHeaderFiltersChange}
                      onRefresh={() => void companiesQ.refetch()}
                      testIdPrefix="companies"
                    />
                  </div>
                </div>
                <QuickFilterBar
                  quickFilters={quickFilters}
                  activeId={activeQuickFilterId}
                  onChange={(id) => {
                    setActiveQuickFilterId(id);
                    controls.resetPage();
                  }}
                />
              </div>
            }
            columns={columns}
            data={companies}
            isLoading={companiesQ.isLoading}
            getRowId={(row) => row.id}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onVisibleColumnsChange={setVisibleColumnIds}
            onRowContextMenu={companyActions.onRowContextMenu}
            headerFilters={headerFilters}
            onHeaderFiltersChange={handleHeaderFiltersChange}
            filterable
            columnFilters={columnFilters}
            onColumnFiltersChange={handleColumnFiltersChange}
            {...controls.tableProps}
            totalItems={companiesQ.data?.total ?? 0}
            isFetching={companiesQ.isFetching}
            onCalendarRangeChange={controls.setCalendarRange}
            onRowClick={(row) => navigate(withMenuItem(`/companies/${row.id}`))}
            renderActions={(row) => (
              <div className="flex justify-end gap-1">
                {/* Firm NIE można usuwać (decyzja właścicielki) — w wierszu tylko edycja. */}
                <button
                  type="button"
                  onClick={() => handleEdit(row)}
                  className="rounded-lg p-1.5 text-text-muted hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-900/20"
                  aria-label={t('common.edit')}
                  data-testid={`companies-row-${row.id}-edit`}
                >
                  <Pencil size={14} aria-hidden="true" />
                </button>
              </div>
            )}
            actionsColumnLabel={t('tenant.companies.column.actions')}
            emptyIcon={Building2}
            emptyTitle={t('tenant.companies.empty')}
          />
        )}
      </div>

      {companyActions.overlays}
    </div>
  );
}
